import { Component, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    FormGroup
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReportService } from '../../../shared/services/report.service';
import { VenueService } from '../../../shared/services/venue.service';
import { TeamService } from '../../../shared/services/team.service';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-audit-report',
    imports: [
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatTableModule,
        MatPaginatorModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatInputModule,
        MatDatepickerModule,
        MatSelectModule,
        MatOptionModule,
        MatAutocompleteModule
    ],
    providers: [provideNativeDateAdapter()],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './audit-report.component.html',
    styleUrl: './audit-report.component.scss'
})
export class AuditReportComponent implements OnInit {
    reportForm: FormGroup;
    isLoading: boolean = false;
    hasSearched: boolean = false;
    venues: any[] = []; 
    courts: any[] = []; 
    isCourtsLoading: boolean = false; 
    isVenuesLoading: boolean = false; 
    userEmails: string[] = [];
    companies: {id: string, name: string}[] = [];
    isEmailsLoading: boolean = false;
    isCompaniesLoading: boolean = false;
    
    // Autocomplete properties
    filteredEmails: string[] = [];

    // Table properties - Only required columns
    displayedColumns: string[] = [
        'bookingId', 
        'venue', 
        'court', 
        'date', 
        'duration', 
        'price', 
        'status', 
        'company', 
        'bookingAmount', 
        'name', 
        'email', 
        'role'
    ];
    dataSource = new MatTableDataSource<any>([]);
    totalItems: number = 0;
    totalAmount: number = 0;
    
    // Pagination properties
    pageSize: number = 10;
    pageIndex: number = 0;
    // Remove allBookings array since we're doing server-side pagination

    @ViewChild(MatPaginator) paginator: MatPaginator;

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private reportService: ReportService,
        private venueService: VenueService,
        private teamsService: TeamService,
        private cdr: ChangeDetectorRef
    ) {
        const today = new Date();
        const month = today.getMonth();
        const year = today.getFullYear();
        
        this.reportForm = this._formBuilder.group({
            email: '',
            companyId: '',
            venueId: '',
            courtId: '',
            startDate: new Date(year, month, 1),
            endDate: new Date(year, month, 29)
        });
    }

    ngOnInit(): void {
        this.fetchVenues();
        this.fetchUsersData();
        this.setupFormListeners();
        this.onSubmit();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        
        // Listen to paginator events
        this.paginator.page.subscribe((event: PageEvent) => {
            this.onPageChange(event);
        });
    }

    // Add this method to listen to form changes
    private setupFormListeners(): void {
        // Listen to email changes with debounce to avoid too many API calls
        this.reportForm.get('email')?.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe((value) => {
                this.filterEmails(value);
                this.resetPaginationAndSearch();
            });

        // Listen to company changes
        this.reportForm.get('companyId')?.valueChanges
            .subscribe(() => {
                this.resetPaginationAndSearch();
            });

        // Listen to venue changes
        this.reportForm.get('venueId')?.valueChanges
            .subscribe((venueId) => {
                this.onVenueChange(venueId);
            });

        // Listen to court changes
        this.reportForm.get('courtId')?.valueChanges
            .subscribe(() => {
                this.resetPaginationAndSearch();
            });

        // Listen to date changes
        this.reportForm.get('startDate')?.valueChanges
            .subscribe(() => {
                this.resetPaginationAndSearch();
            });

        this.reportForm.get('endDate')?.valueChanges
            .subscribe(() => {
                this.resetPaginationAndSearch();
            });
    }

    // Reset pagination when filters change
    private resetPaginationAndSearch(): void {
        this.pageIndex = 0;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.onSubmit();
    }

    // Add this method to filter emails for autocomplete
    filterEmails(value: string): void {
        if (value) {
            const filterValue = value.toLowerCase();
            this.filteredEmails = this.userEmails.filter(email => 
                email.toLowerCase().includes(filterValue)
            );
        } else {
            this.filteredEmails = this.userEmails;
        }
        this.cdr.markForCheck();
    }

    fetchUsersData(): void {
        this.isEmailsLoading = true;
        this.isCompaniesLoading = true;
        this.teamsService.getUsers().subscribe({
            next: (response) => {
                this.isEmailsLoading = false;
                this.isCompaniesLoading = false;
                console.log('Users API response:', response);
                
                let users: any[] = [];
                if (response && Array.isArray(response.results)) {
                    users = response.results;
                } else if (Array.isArray(response)) {
                    users = response;
                }
                
                // Extract emails
                this.userEmails = users
                    .map(user => user.email)
                    .filter(email => email && email.trim() !== '')
                    .sort();
                
                // Extract unique companies
                const companyMap = new Map<string, {id: string, name: string}>();
                users.forEach(user => {
                    if (user.companyId && user.companyId._id && user.companyId.name) {
                        companyMap.set(user.companyId._id, {
                            id: user.companyId._id,
                            name: user.companyId.name
                        });
                    }
                });
                
                this.companies = Array.from(companyMap.values())
                    .sort((a, b) => a.name.localeCompare(b.name));
                
                // Initialize filtered emails
                this.filteredEmails = this.userEmails;
                console.log('Extracted emails:', this.userEmails);
                console.log('Extracted companies:', this.companies);
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.isEmailsLoading = false;
                this.isCompaniesLoading = false;
                console.error('Error fetching users:', error);
                this.userEmails = [];
                this.companies = [];
                this.filteredEmails = [];
                this.cdr.markForCheck();
            }
        });
    }

    fetchVenues(): void {
        this.isVenuesLoading = true;
        this.venueService.getAllVenues().subscribe({
            next: (response) => {
                this.isVenuesLoading = false;
                this.venues = response || [];
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.isVenuesLoading = false;
                console.error('Error fetching venues:', error);
                this.venues = [];
                this.cdr.markForCheck();
            }
        });
    }

    onVenueChange(venueId: string): void {
        if (venueId) {
            this.isCourtsLoading = true;
            this.courts = [];
            this.reportForm.get('courtId')?.setValue('');
            this.cdr.markForCheck();
            
            this.venueService.getCourtsByVenue(venueId).subscribe({
                next: (response) => {
                    this.isCourtsLoading = false;
                    console.log('Courts API response:', response);

                    if (response && response.success && Array.isArray(response.results)) {
                        this.courts = response.results;
                    } else if (Array.isArray(response)) {
                        this.courts = response;
                    } else {
                        this.courts = [];
                    }

                    this.cdr.markForCheck();
                    console.log('Processed courts:', this.courts);
                    
                    // Reset pagination and search after courts are loaded
                    this.resetPaginationAndSearch();
                },
                error: (error) => {
                    this.isCourtsLoading = false;
                    console.error('Error fetching courts:', error);
                    this.courts = [];
                    this.cdr.markForCheck();
                    // Still reset pagination and search even if courts fail to load
                    this.resetPaginationAndSearch();
                }
            });
        } else {
            this.courts = [];
            this.reportForm.get('courtId')?.setValue('');
            this.cdr.markForCheck();
            this.resetPaginationAndSearch();
        }
    }

    onSubmit(): void {
        this.isLoading = true;
        const formData = this.reportForm.value;

        // Add pagination parameters to the API call
        const paginatedFormData = {
            ...formData,
            page: this.pageIndex + 1,
            limit: this.pageSize
        };

        // Use the audit endpoint
        this.reportService.searchBookingsAudit(paginatedFormData).subscribe({
            next: (response) => {
                this.isLoading = false;
                this.hasSearched = true;

                console.log('Audit Bookings API response:', response);

                let results: any[] = [];
                if (response && Array.isArray(response.results)) {
                    results = response.results;
                    this.totalAmount = response.totalAmount || 0;
                    this.totalItems = response.totalCount || 0;
                } else if (Array.isArray(response)) {
                    results = response;
                    this.totalItems = results.length;
                    this.totalAmount = results.reduce((sum, item) => sum + (item.total || 0), 0);
                }

                const processedResults = results.map(item => {
                    const start = new Date(item.start);
                    const end = new Date(item.end);

                    const diffMs = end.getTime() - start.getTime();
                    const diffMinutes = Math.floor(diffMs / (1000 * 60));

                    // Format date for display
                    const formattedDate = start.toLocaleDateString() + ' ' + start.toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    });

                    return {
                        ...item,
                        date: formattedDate,
                        duration: `${diffMinutes}m`,
                        price: item.total || 0,
                        venue: item.venue?.name || 'N/A',
                        court: item.court?.name || 'N/A',
                        company: item.company?.name || 'N/A',
                        name: item.bookedBy?.name || 'N/A',
                        email: item.bookedBy?.email || 'N/A',
                        role: item.bookedBy?.role || 'N/A',
                        bookingAmount: item.bookingAmount || 0
                    };
                });

                // Update table data directly - no client-side pagination needed
                this.dataSource.data = processedResults;
                
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.isLoading = false;
                this.hasSearched = true;
                console.error('Error fetching audit bookings:', error);
                this.cdr.markForCheck();
            }
        });
    }

    clearFilter(): void {
        const today = new Date();
        const month = today.getMonth();
        const year = today.getFullYear();
        
        this.reportForm.patchValue({
            email: '',
            companyId: '',
            venueId: '',
            courtId: '',
            startDate: new Date(year, month, 1),
            endDate: new Date(year, month, 29)
        });
        this.courts = [];
        // Reset filtered emails to show all
        this.filteredEmails = this.userEmails;
        // Reset pagination
        this.resetPaginationAndSearch();
    }

    // Pagination handler
    onPageChange(event: PageEvent): void {
        this.pageSize = event.pageSize;
        this.pageIndex = event.pageIndex;
        this.onSubmit(); 
    }

    // Fix for the Math.min error in template
    getStartIndex(): number {
        return this.pageIndex * this.pageSize;
    }

    getEndIndex(): number {
        return Math.min((this.pageIndex + 1) * this.pageSize, this.totalItems);
    }

    downloadReport(): void {
    this.isLoading = true;
    const formData = this.reportForm.value;

    // ADD PAGINATION PARAMETERS to CSV export
    const exportFormData = {
        ...formData,
        page: this.pageIndex + 1,
        limit: this.pageSize      
    };

    this.reportService.exportBookingsToCSV(exportFormData).subscribe({
        next: (response: Blob) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const csvText = reader.result as string;
                const filteredCSV = this.filterCSVColumns(csvText);
                
                const blob = new Blob([filteredCSV], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-report-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                
                this.isLoading = false;
                this.cdr.markForCheck();
            };
            
            reader.onerror = () => {
                this.isLoading = false;
                this.cdr.markForCheck();
                console.error('Error reading CSV file');
            };
            
            reader.readAsText(response);
        },
        error: (error) => {
            this.isLoading = false;
            this.cdr.markForCheck();
            console.error('Error downloading report:', error);
        }
    });
}
    private filterCSVColumns(csvText: string): string {
        const lines = csvText.split('\n');
        if (lines.length === 0) return '';

        const headers = lines[0].split(',');
        const requiredColumns = [
            'bookingId', 'venue', 'court', 'date', 'duration', 'price', 
            'status', 'company', 'bookingAmount', 'name', 'email', 'role'
        ];
        
        const columnIndices = requiredColumns.map(col => {
            const index = headers.findIndex(header => 
                header.trim().toLowerCase() === col.toLowerCase() || 
                header.trim().toLowerCase().includes(col.toLowerCase())
            );
            return index;
        }).filter(index => index !== -1);

        const filteredLines = lines.map(line => {
            const values = line.split(',');
            const filteredValues = columnIndices.map(index => values[index] || '');
            return filteredValues.join(',');
        });

        return filteredLines.join('\n');
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'confirmed':
                return 'bg-green-100 text-green-800';
            case 'tentative':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
            case 'canceled':
                return 'bg-red-100 text-red-800';
            case 'completed':
                return 'bg-blue-100 text-blue-800';
            case 'no_show':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    }

    getStatusIcon(status: string): string {
        switch (status) {
            case 'confirmed':
                return 'check_circle';
            case 'tentative':
                return 'help';
            case 'cancelled':
            case 'canceled':
                return 'cancel';
            case 'completed':
                return 'done_all';
            case 'no_show':
                return 'no_accounts';
            default:
                return 'help';
        }
    }

    getRoleClass(role: string): string {
        switch (role?.toLowerCase()) {
            case 'admin':
                return 'bg-purple-100 text-purple-800';
            case 'manager':
                return 'bg-blue-100 text-blue-800';
            case 'staff':
                return 'bg-green-100 text-green-800';
            case 'player':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    }

    // Helper methods for venue and court names
    getVenueName(venueId: any): string {
        if (venueId && typeof venueId === 'object' && venueId.name) {
            return venueId.name;
        }
        const venue = this.venues.find(v => v._id === venueId || v.id === venueId);
        return venue ? (venue.name || venue.venueName || venue._id) : venueId;
    }

    getCourtName(courtId: any): string {
        if (courtId && typeof courtId === 'object' && courtId.name) {
            return courtId.name;
        }
        const court = this.courts.find(c => c._id === courtId || c.id === courtId);
        return court ? (court.name || court.courtName || court.title || court._id) : courtId;
    }
}